import React from "react";
import moment from "moment";
import { Typography, Paper, Grid as GridBase } from "@mui/material";
import { Icon } from "@mui/material";

import RpMUIModal from "@/components/modals/RpMUIModal";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import useSearchFilter from "@/helpers/hooks/useSearchFilter";
import NoDataFoundBase from "@/components/NoDataFound";
import { getStatusCode } from "@/helpers/utils";
import MuiSearchInput from "@/components/muiCustom/search/MuiSearchInput";
import { DIRECT_MAIL_CAMP_PROVIDER } from "@/features/campaigns/direct-mail/utils/directMailConstants";

type DirectMailLocation = {
  id?: string | number;
  locationName?: string;
  location_name?: string;
  campaign_status?: string | number;
  status?: string | number;
  schedule_date?: string;
  mailing_week_range?: string;
  [key: string]: any;
};

type DirectMailLocationListModalProps = {
  locations?: DirectMailLocation[];
  showLocationModal?: boolean;
  setShowLocationModal?: React.Dispatch<React.SetStateAction<boolean>>;
  getCampaignProvider?: string | null;
};

const Grid = GridBase as React.ComponentType<any>;
const NoDataFound = NoDataFoundBase as React.ComponentType<any>;

const DirectMailLocationListModal = ({
  locations = [],
  showLocationModal = false,
  setShowLocationModal = () => {},
  getCampaignProvider = null,
}: DirectMailLocationListModalProps) => {
  // Search functionality using custom hook
  const locationNameKey =
    getCampaignProvider === DIRECT_MAIL_CAMP_PROVIDER.RRD
      ? "location_name"
      : "locationName";
  const [searchTerm, handleSearchChange, filteredLocations] = useSearchFilter(
    locations,
    [locationNameKey]
  );

  const onClose = () => {
    setShowLocationModal(false);
    handleSearchChange("");
  };
  const getMailingDates = (loc: DirectMailLocation) => {
    if (loc?.schedule_date) {
      const start = moment(loc.schedule_date);
      const end = start.isValid() ? moment(start).add(4, "day") : null;
      if (start.isValid() && end) {
        return `${start.format("MM/DD/YYYY")} - ${end.format("MM/DD/YYYY")}`;
      }
    }
    if (loc?.mailing_week_range) {
      return loc.mailing_week_range;
    }
    return "N/A";
  };

  return (
    <>
      <RpMUIModal
        open={showLocationModal}
        onClose={() => {
          onClose();
          handleSearchChange("");
        }}
        maxWidth="sm"
        fullWidth
        className="p-0"
      >
        <div className="location-list-modal p-1">
          <Icon
            component="img"
            src="/img/icons/black-cross-icon.png"
            className="close-icon"
            onClick={() => {
              onClose();
              handleSearchChange("");
            }}
          />
          {/* Search Field */}
          <Grid
            container
            spacing={1}
            className="mb-1"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item size={{ xs: 12, sm: 11, md: 11, lg: 11 }}>
              <MuiSearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                type="search"
                variant="outlined"
                placeholder="Search.."
                className="mt-3 mt-lg-1 mt-lx-0"
              />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            className="px-2 py-2 border-bottom"
            sx={{
              backgroundColor: "var(--neutral-100)",
              display: { xs: "none", sm: "block" },
            }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                size={{ xs: 4, sm: 4, md: 4, lg: 4 }}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <Typography variant="caption" className="fw-sb mb-0">
                  Location Name
                </Typography>
              </Grid>

              <Grid
                item
                size={{ xs: 4, sm: 4, md: 4, lg: 4 }}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" className="fw-sb mb-0">
                  Status
                </Typography>
              </Grid>

              <Grid
                item
                size={{ xs: 4, sm: 4, md: 4, lg: 4 }}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Typography variant="caption" className="fw-sb mb-0 mr-0">
                  Mailing Date
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Location List */}
          <div className="location-list-container py-1">
            {filteredLocations.length === 0 ? (
              <NoDataFound message="No locations found" />
            ) : (
              <>
                {filteredLocations.map((loc, index) => (
                  <Paper key={index} elevation={0} className="px-1 py-2">
                    <Grid container spacing={2}>
                      <Grid
                        item
                        size={{ xs: 12, sm: 4, md: 4, lg: 4 }}
                        className="d-flex align-items-center justify-content-start"
                      >
                        <Typography
                          className="text-truncate fw-sb"
                          title={loc.locationName}
                          variant="body2"
                        >
                          {loc.locationName
                            ? loc.locationName
                            : loc.location_name}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        size={{ xs: 12, sm: 4, md: 4, lg: 4 }}
                        className="d-flex align-items-center justify-content-sm-center justify-content-xs-start"
                      >
                        <StatusChip
                          label={loc.campaign_status || loc.status}
                          status={getStatusCode(
                            loc.campaign_status || loc.status,
                            "hold"
                          )}
                          showOnlyLabel
                        />
                      </Grid>
                      <Grid
                        item
                        size={{ xs: 12, sm: 4, md: 4, lg: 4 }}
                        className="d-flex align-items-center justify-content-sm-end justify-content-xs-start"
                      >
                        <Typography variant="body2">
                          {getMailingDates(loc)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </>
            )}
          </div>
        </div>
      </RpMUIModal>
    </>
  );
};

export default DirectMailLocationListModal;
